import React, { Fragment, useEffect, useState } from "react";
import { oneOfType, array, string, bool, shape } from "prop-types";
import { connect, useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import isEmpty from "lodash/isEmpty";

import moment from "moment";

import { actions as loansActions, selectors as loansSelectors } from "reducers/loans";

import Notification from "pages/_components/Notification";
import MainContainer from "pages/_components/MainContainer";
import Container from "pages/_components/Container";
import I18n from "pages/_components/I18n";
import GridTable from "pages/_components/GridTable/GridTable";
import * as i18n from "util/i18n";
import CustomText from "pages/_components/CustomText/CustomText";
import "react-circular-progressbar/dist/styles.css";
import FormattedAmount from "pages/_components/FormattedAmount";
import CustomData from "pages/_components/CustomData/CustomData";
import { ResponsiveHeader } from "pages/_components/header";
import getCellPlacement from "../../../../util/helpers/table";
import LoanDetailViewMore from "./LoanDetailViewMore";
import LoanDetailFees from "./LoanDetailFees";

const DetallePrestamo = ({ fetching, activeLoans, isDesktop, detallePrestamo }) => {
    const dispatch = useDispatch();
    const { id } = useParams();
    const [currentLoan, setCurrentLoan] = useState(null);

    useEffect(() => {
        dispatch(loansActions.getLoanDetail(id));
    }, []);

    useEffect(() => {
        if (currentLoan == null) {
            const newCurrentLoan = activeLoans.find((x) => x.nroPrestamo === id);
            setCurrentLoan(newCurrentLoan);
        }
    }, [activeLoans]);

    const contentTable = ({ prestamoActual }) => {
        const getCellProps = (row, col) => getCellPlacement(row, col, { isDesktop });
        const activeFee = detallePrestamo.cuotas.find((x) => x.nroCuota === Number(detallePrestamo.cuotaActiva));
        return (
            <>
                <GridTable.Data
                    columnWidth={1}
                    rowWidth={1}
                    alignX="left"
                    alignY="start"
                    flexDirection={isDesktop ? "row" : "column"}
                    {...getCellProps(1, 1)}>
                    <CustomData
                        titleText={i18n.get("unsecured.loans.detail.fees.activeFee")}
                        titleTextType="STRING"
                        valueText={<CustomText text={`Cuota ${detallePrestamo.cuotaActiva}`} />}
                        valueTextType="STRING"
                    />
                </GridTable.Data>

                <GridTable.Data
                    columnWidth={1}
                    rowWidth={1}
                    alignX="left"
                    alignY="start"
                    flexDirection={isDesktop ? "row" : "column"}
                    {...getCellProps(1, 2)}>
                    <CustomData
                        titleText={i18n.get("unsecured.loans.confirm.expirationDate")}
                        titleTextType="STRING"
                        valueText={
                            <CustomText
                                text={
                                    activeFee.fechaVencimiento && (moment(activeFee.fechaVencimiento).utc().format("DD/MM/YYYY"))
                                }
                            />
                        }
                        valueTextType="STRING"
                    />
                </GridTable.Data>
                <GridTable.Data
                    columnWidth={1}
                    rowWidth={1}
                    alignX="left"
                    alignY="start"
                    flexDirection={isDesktop ? "row" : "column"}
                    {...getCellProps(1, 3)}>
                    <CustomData
                        titleText={i18n.get("unsecured.loans.detail.fees.debitAccount")}
                        titleTextType="STRING"
                        valueText={
                            <CustomText
                                text={`${i18n.get(`productType.${detallePrestamo?.tipoCuenta}`)} 
                                ····${detallePrestamo?.numeroCuenta?.slice(-4)}
                                ${i18n.get("currency.label.ARS")}`}
                            />
                        }
                        valueTextType="STRING"
                    />
                </GridTable.Data>

                <GridTable.Data
                    columnWidth={1}
                    rowWidth={1}
                    alignX="left"
                    alignY="start"
                    flexDirection={isDesktop ? "row" : "column"}
                    {...getCellProps(1, 4)}>
                    <CustomData
                        titleText={i18n.get("unsecured.loans.detail.fees.amountToPay")}
                        titleTextType="STRING"
                        valueText={<FormattedAmount currency="ARS" quantity={prestamoActual.montoAcreditado} />}
                        valueTextType="STRING"
                    />
                </GridTable.Data>
            </>
        );
    };

    const renderBody = () => {
        const prestamoActual = activeLoans.find((x) => x.nroPrestamo === Number(id));
        const columns = isDesktop ? 4 : 2;
        const rows = isDesktop ? 1 : 3;
        const columnsTemplate = isDesktop ? `repeat(3, 1fr) ` : "1fr";

        if (prestamoActual === undefined || prestamoActual === null || !detallePrestamo || isEmpty(detallePrestamo)) {
            return null;
        }

        return (
            <div className="above-the-fold">
                <Fragment>
                    <Container className="container--layout flex-grow">
                        <div
                            className="box"
                            style={{ backgroundColor: "#d9d9d9" }}>
                            <CustomText
                                text="unsecured.loans.detail.fees.title"
                                textType="I18N"
                                className="loans-box-title" />
                            <GridTable>
                                <GridTable.Body>
                                    <GridTable.Container
                                        className="px-0"
                                        key="1551"
                                        columns={columns}
                                        rows={rows}
                                        renderAs="div"
                                        columnsTemplate={columnsTemplate}>
                                        {contentTable({ prestamoActual })}
                                    </GridTable.Container>
                                </GridTable.Body>
                            </GridTable>
                            <I18n id="unsecured.loans.detail.fees.disclaimer" />
                        </div>

                        <LoanDetailViewMore
                            activeLoans={activeLoans}
                            isDesktop={isDesktop}
                            detallePrestamo={detallePrestamo}
                            idLoan={id}
                        />

                        <LoanDetailFees isDesktop={isDesktop} detallePrestamo={detallePrestamo} />
                    </Container>
                </Fragment>
            </div>
        );
    };

    return (
        <>
            <ResponsiveHeader backLinkTo="/loans" defaultTitle="Préstamo" />

            <MainContainer showLoader={fetching} className="desktop loans-detail-container">
                <CustomData titleText={`Nº ${id}`} />
                <Notification scopeToShow="loans" />
                <div className="loans-detail">{renderBody()}</div>
            </MainContainer>
        </>
    );
};

DetallePrestamo.propTypes = {
    detallePrestamo: shape({}).isRequired,
    activeLoans: oneOfType([array]).isRequired,
    isDesktop: oneOfType([string, bool]),
    fetching: oneOfType([string, bool]),
};

DetallePrestamo.defaultProps = {
    isDesktop: false,
    fetching: false,
};

const mapStateToProps = (state) => ({
    fetching: loansSelectors.getFetching(state),
    activeLoans: loansSelectors.getActiveLoans(state),
    detallePrestamo: loansSelectors.getDetallePrestamo(state),
});

export default connect(mapStateToProps)(DetallePrestamo);
